/**
 * GOVERNANCE OPERATIONAL VISUAL LANGUAGE V1 — Color Semantics
 *
 * Canonical operational color palette and semantic state → color mappings.
 *
 * DOCTRINE:
 * The operational UI is predominantly neutral/dark. Color fires ONLY when
 * governance semantics require it:
 *   refusal · degradation · interruption · unresolved · boundary violation ·
 *   replay divergence · stabilization · admissibility failure
 *
 * Color is signal, not decoration. Reduce color entropy.
 *
 * ANTI-PATTERNS (forbidden):
 * - Neon dashboards or gradient-heavy backgrounds
 * - Marketing primary-blue everywhere
 * - Consumer app accent colors
 * - Color that carries no governance semantics
 * - Saturated fills on row backgrounds
 *
 * INVARIANTS:
 * - All hex values are readonly.
 * - Semantic mappings are pure functions: same state → same colors.
 * - Dark-first (workstation default). Light variant reserved for future.
 * - Chip text meets WCAG AA (4.5:1) against chip background.
 * - Row background tints are low-opacity — NOT saturated fills.
 *
 * Schema version: governance-operational-visual-language/v1
 */

import type { OperationalSeverity, ContinuityStateMarker } from '../operationalRendering/types.js';

// ---------------------------------------------------------------------------
// Neutral scale — backbone of the operational surface
// ---------------------------------------------------------------------------

/**
 * NEUTRAL — 90%+ of the UI surface uses these values.
 *
 * Rule: Background surfaces MUST stay within bg100–bg600.
 * Rule: Text MUST stay within text100–text700.
 * Rule: Borders use border or borderSubtle only.
 */
export const NEUTRAL = {
  bg100: '#0d0d0f',       // App background
  bg200: '#111113',       // Pane background
  bg300: '#161618',       // Secondary pane / inset
  bg400: '#1c1c1f',       // Hover row
  bg500: '#222226',       // Selected row
  bg600: '#28282d',       // Pane header / section bar
  bg700: '#2f2f36',       // Active/focused control
  borderSubtle: '#1e1e23',
  border: '#28282d',
  borderStrong: '#363640',
  text100: '#3a3a44',     // Disabled
  text200: '#55555f',     // De-emphasized, muted timestamps
  text300: '#7a7a88',     // Secondary metadata, captions
  text400: '#9a9aaa',     // Inspector values, row labels
  text500: '#c0c0d0',     // Primary body
  text600: '#d8d8e8',     // Emphasized — span headers, pane labels
  text700: '#eeeef8',     // Maximum contrast — selected / critical
} as const;

// ---------------------------------------------------------------------------
// Semantic palette
// ---------------------------------------------------------------------------

/**
 * SemanticColor — one governance state's full visual expression.
 *
 * bg:       Very low-opacity row tint. NEVER a saturated fill.
 * border:   Subtle left-border accent for selected rows.
 * text:     Text color for labels in this state.
 * chip:     Badge background (solid, used in chips).
 * chipText: Badge text (must contrast against chip).
 * marker:   Timeline lane dot / continuity line color.
 * glyph:    Icon/symbol tint for state indicators.
 */
export interface SemanticColor {
  bg: string;
  border: string;
  text: string;
  chip: string;
  chipText: string;
  marker: string;
  glyph: string;
}

export const SEMANTIC: Record<string, SemanticColor> = {
  /**
   * stable — healthy, governed, expected normal.
   * Minimal visual weight. Stability is the baseline.
   */
  stable: {
    bg: 'transparent',
    border: '#2a3a2a',
    text: '#6a9f6a',
    chip: '#172217',
    chipText: '#5fa35f',
    marker: '#3d7a3d',
    glyph: '#4a8a4a',
  },

  /**
   * degraded — continuity has dropped; output may be compromised.
   * Amber. High visibility without critical alarm level.
   */
  degraded: {
    bg: '#15110600',     // transparent overlay — applied via CSS opacity
    border: '#5a3a00',
    text: '#c89010',
    chip: '#251a00',
    chipText: '#d4a017',
    marker: '#c8860a',
    glyph: '#c8860a',
  },

  /**
   * interrupted — cancelled, timed out, abruptly halted.
   * Orange. Distinct from degraded (amber) and refusal (red).
   */
  interrupted: {
    bg: 'transparent',
    border: '#5a2800',
    text: '#c86020',
    chip: '#221200',
    chipText: '#e07030',
    marker: '#c85820',
    glyph: '#c85820',
  },

  /**
   * unresolved — ambiguous; action_required; cannot determine status.
   * Muted indigo/purple. Not alarming but distinct from stable.
   */
  unresolved: {
    bg: 'transparent',
    border: '#3a2860',
    text: '#8870c8',
    chip: '#1a1430',
    chipText: '#9880d8',
    marker: '#7060c0',
    glyph: '#7060c0',
  },

  /**
   * stabilized — system recovered from degraded/interrupted.
   * Teal/cyan. Distinct from stable (green): signals recent recovery.
   */
  stabilized: {
    bg: 'transparent',
    border: '#1a4040',
    text: '#30a0a0',
    chip: '#0a2020',
    chipText: '#30b8b8',
    marker: '#259090',
    glyph: '#259090',
  },

  /**
   * refusal — explicit block: REFUSAL event, BLOCKED gate.
   * Red. The strongest signal in the UI.
   * Rule: Red is reserved EXCLUSIVELY for refusal/critical severity.
   */
  refusal: {
    bg: '#120808',
    border: '#6a1818',
    text: '#e03030',
    chip: '#220808',
    chipText: '#f04040',
    marker: '#d02020',
    glyph: '#d02020',
  },

  /**
   * boundaryViolation — governance boundary crossed.
   * Deep red/crimson. Slightly different hue from refusal to distinguish.
   */
  boundaryViolation: {
    bg: '#130808',
    border: '#6a1020',
    text: '#d82840',
    chip: '#200810',
    chipText: '#e83850',
    marker: '#c82030',
    glyph: '#c82030',
  },

  /**
   * admissible — gate passed, all rules satisfied.
   * Soft green, lower saturation than stable. Contextual confirmation.
   */
  admissible: {
    bg: 'transparent',
    border: '#203a20',
    text: '#58a058',
    chip: '#102010',
    chipText: '#68b068',
    marker: '#509050',
    glyph: '#509050',
  },

  /**
   * rejected — explicitly rejected (not just blocked).
   * Rose/magenta — distinct from refusal red.
   */
  rejected: {
    bg: '#120a10',
    border: '#601040',
    text: '#c82880',
    chip: '#200814',
    chipText: '#d83898',
    marker: '#b82070',
    glyph: '#b82070',
  },

  /**
   * informational — neutral governance events: GHA steps, outputs, trace steps.
   * Muted blue-grey. Low visual weight.
   */
  informational: {
    bg: 'transparent',
    border: '#202840',
    text: '#6080a8',
    chip: '#101820',
    chipText: '#7090b8',
    marker: '#506888',
    glyph: '#506888',
  },

  /**
   * replayWindow — events inside a replay window overlay.
   * Muted blue tint on timeline rows. Non-alarming.
   */
  replayWindow: {
    bg: '#0a0e16',
    border: '#1a2840',
    text: '#5878a8',
    chip: '#0c1420',
    chipText: '#6888b8',
    marker: '#4868a0',
    glyph: '#4868a0',
  },

  /**
   * proofOnly — events with proofOnly=true.
   * Muted grey-violet. Signals restricted visibility.
   */
  proofOnly: {
    bg: 'transparent',
    border: '#303040',
    text: '#706880',
    chip: '#1a1820',
    chipText: '#807898',
    marker: '#605870',
    glyph: '#605870',
  },

  /**
   * unknown — no determinable state.
   * Neutral grey. Lowest visual weight.
   */
  unknown: {
    bg: 'transparent',
    border: NEUTRAL.borderSubtle,
    text: NEUTRAL.text200,
    chip: NEUTRAL.bg400,
    chipText: NEUTRAL.text300,
    marker: NEUTRAL.text200,
    glyph: NEUTRAL.text200,
  },
} as const;

// ---------------------------------------------------------------------------
// Severity → color mapping
// ---------------------------------------------------------------------------

/**
 * Map OperationalSeverity to a SemanticColor.
 *
 * Pure function. Deterministic. Same severity → same color.
 */
export function severityToColor(severity: OperationalSeverity): SemanticColor {
  switch (severity) {
    case 'critical': return SEMANTIC['boundaryViolation'];
    case 'high':     return SEMANTIC['refusal'];
    case 'medium':   return SEMANTIC['degraded'];
    case 'low':      return SEMANTIC['informational'];
    case 'info':     return SEMANTIC['informational'];
    case 'unknown':  return SEMANTIC['unknown'];
  }
}

// ---------------------------------------------------------------------------
// ContinuityStateMarker → color mapping
// ---------------------------------------------------------------------------

/**
 * Map ContinuityStateMarker to a SemanticColor.
 *
 * Pure function. Deterministic. Same marker → same color.
 */
export function continuityToColor(marker: ContinuityStateMarker): SemanticColor {
  switch (marker) {
    case 'stable':      return SEMANTIC['stable'];
    case 'degraded':    return SEMANTIC['degraded'];
    case 'recovering':  return SEMANTIC['unresolved'];
    case 'interrupted': return SEMANTIC['interrupted'];
    case 'unresolved':  return SEMANTIC['unresolved'];
    case 'stabilized':  return SEMANTIC['stabilized'];
    case 'unknown':     return SEMANTIC['unknown'];
  }
}

// ---------------------------------------------------------------------------
// Verdict → color mapping
// ---------------------------------------------------------------------------

/**
 * Map an admissibility verdict to a SemanticColor.
 *
 * Pure function. Deterministic.
 */
export function verdictToColor(
  verdict: 'PASS' | 'BLOCKED' | 'REJECTED' | 'UNKNOWN'
): SemanticColor {
  switch (verdict) {
    case 'PASS':    return SEMANTIC['admissible'];
    case 'BLOCKED': return SEMANTIC['refusal'];
    case 'REJECTED': return SEMANTIC['rejected'];
    case 'UNKNOWN': return SEMANTIC['unknown'];
  }
}

// ---------------------------------------------------------------------------
// Source tag → chip color
// ---------------------------------------------------------------------------

import type { OperationalSourceTag } from '../operationalRendering/types.js';

/**
 * SourceTagChipColor — a minimal chip color for source tag badges.
 *
 * Source tags use muted, low-saturation chips — they are metadata, not alarms.
 */
export interface SourceTagChipColor {
  chip: string;
  chipText: string;
}

export const SOURCE_TAG_CHIP_COLORS: Record<OperationalSourceTag, SourceTagChipColor> = {
  'github-actions':   { chip: '#0e1820', chipText: '#5888b8' },
  'ratchet-chain':    { chip: '#181420', chipText: '#8070b0' },
  'lifecycle':        { chip: '#101818', chipText: '#409898' },
  'state-machine':    { chip: '#141420', chipText: '#7878b0' },
  'execution-gating': { chip: '#201410', chipText: '#a06040' },
  'autonomous-loop':  { chip: '#141820', chipText: '#5878a0' },
  'evolution-trace':  { chip: '#181820', chipText: '#7080a8' },
  'replay':           { chip: '#0e1420', chipText: '#486898' },
  'verifier':         { chip: '#181418', chipText: '#887090' },
  'console':          { chip: '#181818', chipText: '#888888' },
  'unknown':          { chip: NEUTRAL.bg400, chipText: NEUTRAL.text300 },
} as const;

/**
 * Map OperationalSourceTag to its chip colors.
 * Pure function. Deterministic.
 */
export function sourceTagToChipColor(tag: OperationalSourceTag): SourceTagChipColor {
  return SOURCE_TAG_CHIP_COLORS[tag] ?? SOURCE_TAG_CHIP_COLORS['unknown'];
}

// ---------------------------------------------------------------------------
// Selection / highlight colors
// ---------------------------------------------------------------------------

/**
 * SELECTION_COLORS — colors for the synchronized selection cursor.
 *
 * Rule: Only ONE row is selected at a time per selection dimension.
 * Rule: Selected row uses SELECTION_COLORS.rowBg, NOT a semantic color bg.
 * Rule: The selection accent border is ALWAYS selectionAccent (blue-white).
 */
export const SELECTION_COLORS = {
  rowBg: '#1e2030',
  rowBgHover: '#191924',
  leftAccentBorder: '#4868d0',
  cursorLine: '#3050b8',
  cursorLineOpacity: 0.6,
  focusRing: '#4060c0',
} as const;

// ---------------------------------------------------------------------------
// Timeline overlay colors
// ---------------------------------------------------------------------------

/**
 * TIMELINE_OVERLAY — colors for timeline overlay decorations.
 *
 * Rule: Replay window overlay is a very low-opacity blue tint — NOT opaque.
 * Rule: Refusal overlays are a narrow red vertical indicator, not a block fill.
 * Rule: Continuity transition markers are single-color dots on the continuity lane.
 */
export const TIMELINE_OVERLAY = {
  replayWindowBg: 'rgba(48, 80, 176, 0.10)',
  replayWindowBorder: 'rgba(72, 104, 208, 0.40)',
  refusalOverlay: SEMANTIC['refusal'].marker,
  boundaryOverlay: SEMANTIC['boundaryViolation'].marker,
  continuityTransitionDot: '#d0d0e0',
  proofOnlyStripe: 'rgba(96, 88, 112, 0.08)',
  cursorLine: 'rgba(72, 104, 208, 0.80)',
} as const;
